/**
 * WebSocket Service — manages device WebSocket connections with auto-reconnection
 *
 * INITIAL CONNECT:
 * - Get subscription → Create WebSocket client
 * - On connect → start listening to messages
 *
 * RECONNECT:
 * - Get subscription → Create WebSocket client
 * - On connect → start listening to messages → update device data:
 *   1. fetch ConnectionStatus
 *   2. fetch Assets
 *   3. Sync device (non-retail only)
 *
 * CLOSE CODES:
 * - 1000 (normal close) → do nothing
 * - 4000 (subscription expired) → RECONNECT
 * - other (unexpected close) → RECONNECT
 *
 * ERROR HANDLING:
 * - On WebSocket error → RECONNECT
 * - On connect failure → schedule RECONNECT after 30s
 *
 * ONBOARDING GUARD:
 * - If onboarding is in progress, reconnect is deferred (scheduled after 30s timeout)
 * - Once onboarding finishes, the next scheduled attempt proceeds normally
 */

import {selectIsRetail} from '../../state/selectors/global';
import {selectIotDeviceAssetId, selectIotDevicesDusnList} from '../../state/selectors/iotDevices';
import {selectIsOnboardingInProgress} from '../../state/selectors/session';
import {getState} from '../../state/store';
import log from '../logger/log';
import damClientService from './damClientService';
import damDeviceService from './damDeviceService';
import damService from './damService';

const RECONNECT_TIMEOUT = 30 * 1000; // 30 seconds

const onReconnect = async (serialNumber, servicePrefix) => {
    log.debug(`${servicePrefix}: WebSocket reconnected for ${serialNumber}, updating device data`);

    await damService.getDeviceConnectionStatus(serialNumber);
    await damService.setDeviceAssets(serialNumber);

    const isRetail = selectIsRetail(getState());

    if (!isRetail) {
        await damDeviceService.synchronizeDevice(serialNumber);
    }
};

export const createWebSocketService = (config) => {
    const {servicePrefix, WebSocketClientClass, responseService, asyncRequestService} = config;

    const reconnectTimers = {};

    const getAssetId = (serialNumber) => selectIotDeviceAssetId(serialNumber)(getState());

    const getClient = (assetId, isCreateNew, options, serialNumber) =>
        new WebSocketClientClass(assetId, isCreateNew, options, serialNumber);

    const isConnected = (serialNumber) => {
        const assetId = getAssetId(serialNumber);

        return getClient(assetId).isConnectedCheck(assetId);
    };

    const cancelReconnect = (serialNumber) => {
        clearTimeout(reconnectTimers[serialNumber]);
        delete reconnectTimers[serialNumber];
    };

    const scheduleReconnect = (serialNumber) => {
        cancelReconnect(serialNumber);

        log.debug(`${servicePrefix}: Scheduling reconnect for ${serialNumber} in ${RECONNECT_TIMEOUT}ms`);

        reconnectTimers[serialNumber] = setTimeout(() => {
            delete reconnectTimers[serialNumber];
            reconnect(serialNumber);
        }, RECONNECT_TIMEOUT);
    };

    const reconnect = async (serialNumber) => {
        // Defer reconnect during onboarding — will retry after timeout when setup completes
        if (selectIsOnboardingInProgress(getState())) {
            log.info(`${servicePrefix}: Onboarding in progress, deferring reconnect for ${serialNumber}`);
            scheduleReconnect(serialNumber);
            return;
        }

        try {
            log.info(`${servicePrefix}: Reconnecting ${serialNumber}`);
            await init({serialNumber, isForce: true});
        } catch (e) {
            log.error(`${servicePrefix}: Error during reconnect for ${serialNumber}`, serialNumber, e);
        }
    };

    const attachHandlers = (wsClient, assetId, serialNumber, isForce, isInitial) => {
        wsClient.attachCloseHandler((message) => {
            switch (message?.code) {
                case 1000:
                    // Normal close — no action needed
                    break;
                case 4000:
                    // Subscription expired — reconnect
                    reconnect(serialNumber);
                    break;
                default:
                    // Unexpected close — reconnect
                    reconnect(serialNumber);
                    break;
            }
        });

        wsClient.attachConnectHandler(async () => {
            wsClient.attachMessageHandler(async (message) => {
                await responseService.proceedMessage(message, assetId);
            });

            // Run post-reconnect flow only for reconnects
            if (isForce && !isInitial) {
                await onReconnect(serialNumber, servicePrefix);
            }
        });

        wsClient.attachErrorHandler(() => reconnect(serialNumber));
    };

    const init = async (options) => {
        const {serialNumber, isForce = false, isInitial = false} = options;

        if (isConnected(serialNumber)) return;

        log.info(
            `${servicePrefix}: Initializing WebSocket for ${serialNumber}, isForce: ${isForce}, isInitial: ${isInitial}`,
            serialNumber
        );

        try {
            const subscription = await damClientService.getSubscription(serialNumber);
            const assetId = getAssetId(serialNumber);

            const wsClient = getClient(
                assetId,
                true,
                {url: subscription.url, expiresAt: subscription.expiresAt},
                serialNumber
            );

            attachHandlers(wsClient, assetId, serialNumber, isForce, isInitial);
        } catch (e) {
            log.error(`${servicePrefix}: Failed to initialize WebSocket for ${serialNumber}`, e?.message);
            scheduleReconnect(serialNumber);
        }
    };

    const disconnect = (serialNumber) => {
        log.info(`${servicePrefix}: Disconnecting WebSocket for ${serialNumber}`, serialNumber);

        const assetId = getAssetId(serialNumber);
        const wsClient = getClient(assetId);

        cancelReconnect(serialNumber);

        wsClient.disconnectClient();
        asyncRequestService.clearAsyncRequests(serialNumber);
    };

    const disconnectAll = () => {
        const dusnList = selectIotDevicesDusnList(getState());

        dusnList.forEach((dusn) => disconnect(dusn));
    };

    // Simulates an incoming WebSocket message (used for testing/mocking).
    const messageWS = (args) => {
        responseService.proceedMessage({data: JSON.stringify(args)});
    };

    return {
        initWebSocket: init,
        disconnectWebSocket: disconnect,
        getIsWebSocketConnected: isConnected,
        disconnectAllWebSockets: disconnectAll,
        messageWS,
    };
};
